import {
  envIsDev,
  envIsIntegration,
  envIsServerDeployment,
  serverEnableCoreWeaveObservability,
} from '../../config';
import {RampIdType} from '../../generated/graphql';
import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {type Viewer} from '../../state/viewer/types';
import {useRecordFeatureFlag} from '../datadog/recordFeatureFlag';
import {useIsCurrentProjectNgwEligible} from '../dynamicConfigs/ngwTruncatedPanelGenLimits';
import {setNgwEnabled} from '../ngwFeatureFlag';
import {
  setSparseCompressionEnabled,
  setSparseOverridesEnabled,
} from '../sparseOverridesFeatureFlag';
import {
  useEntityRampFlag,
  useEntityRampFlagWithLoading,
  useOrgIdRampFlag,
  useOrgNameRampFlag,
  useOrgRampFlag,
  useOrgRampFlagWithLoading,
  useUserOrOrgRampFlag,
  useUserRampFlag,
  useUserRampFlagWithLoading,
} from '../useRampFlag';
import {useProjectRsaReadEnabled} from './projectRsaReadEnabled.query';

/*
Docs for ramp feature flags:
https://www.notion.so/wandbai/Ramp-Feature-Flag-Documentation-da56991072df4c8d980cd36804f52ded
*/

/**
 * This is a little hacky but we're wanting to safely prevent special orgs from getting
 * force enrolled in full fidelity until we're ready.
 * 1. The entity name check won't always work because not all workspaces are under namespaces like NVIDIA
 * 2. The `userInfo.company` field is not always populated
 * 3. viewer orgs is pretty reliable, so between all three of these this should be a comprehensive block
 */
// https://weightsandbiases.slack.com/archives/C062UKHTAHG/p1727116018580919
const BLOCK_LIST = ['OPENAI', 'NVIDIA'];

export const getShouldBlockEntity = (
  entityName: string,
  viewer: Viewer | undefined
) => {
  // block special entities
  const isEntityNameBlocked = BLOCK_LIST.includes(entityName.toUpperCase());

  // block based on viewer org
  const isViewerOrgBlocked = viewer?.organizations?.some(org => {
    return BLOCK_LIST.includes(org.name.toUpperCase());
  });

  // block based on userInfo.company
  const company = viewer?.userInfo?.company ?? '';
  const isCompanyBlocked = BLOCK_LIST.includes(company.toUpperCase());

  return isEntityNameBlocked || isViewerOrgBlocked || isCompanyBlocked;
};

// in W&B server yet
export const useRampFlagAccountSelector = () => {
  if (envIsServerDeployment) {
    return false;
  }
  return true;
};

export const useRampFlagInferenceCatalog = (orgName: string) => {
  const ret = useOrgNameRampFlag(
    orgName,
    'inference-catalog',
    RampIdType.OrgName
  );
  const value = envIsServerDeployment
    ? false
    : ((!ret.loading && ret.data) ?? false);
  useRecordFeatureFlag('inference_catalog', value);
  return value;
};

export const useRampFlagStarredProjects = () => {
  const value = useUserRampFlag('starred-projects', RampIdType.UserName);
  useRecordFeatureFlag('starred_projects', value);
  return value;
};

// For call sites that must not act until the flag has resolved (e.g. tables
// whose column set depends on it).
export const useRampFlagStarredProjectsWithLoading = () => {
  const result = useUserRampFlagWithLoading(
    'starred-projects',
    RampIdType.UserName
  );
  useRecordFeatureFlag('starred_projects', result.isEnabled);
  return result;
};

export const useRampFlagHideSummaryHistograms = (entityName: string) => {
  const value = useEntityRampFlag(
    entityName,
    'hide-summary-histograms',
    RampIdType.EntityName
  );
  useRecordFeatureFlag('hide_summary_histograms', value);
  return value;
};

export const useRampFlagGQLLongTermCache = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'gql-long-term-cache-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'gql-long-term-cache-entity',
    RampIdType.EntityName
  );
  const value = isUserEnabled || isEntityEnabled;
  useRecordFeatureFlag('gql_long_term_cache', value);
  return value;
};

export const useRampFlagOrgLevelServiceAccounts = (orgName: string) => {
  const result = useOrgNameRampFlag(
    orgName,
    'org-level-service-accounts',
    RampIdType.OrgName
  );
  useRecordFeatureFlag('org_level_service_accounts', result.data);
  return result;
};

export const useRampFlagEmbeddedPanels = (entityName: string) => {
  const isUserEnabled = useUserRampFlag(
    'embedded-panels-user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'embedded-panels-entity',
    RampIdType.EntityName
  );
  const isDisabled = envIsServerDeployment || envIsIntegration;
  const userValue = isDisabled ? false : isUserEnabled;
  const entityValue = isDisabled ? false : isEntityEnabled;
  const isEnabled = userValue || entityValue;
  useRecordFeatureFlag('embedded_panels', isEnabled);
  return isEnabled;
};

export const useRampFlagTeamReadmeSection = () => {
  const value = useUserRampFlag('team-readme-section', RampIdType.UserName);
  useRecordFeatureFlag('team_readme_section', value);
  return value;
};

export const useRampFlagIssuesObservability = (entityName: string) => {
  const userOrOrgFlag = useUserOrOrgRampFlag(
    'issues-observability-user',
    'issues-observability-org'
  );
  const entityFlag = useEntityRampFlag(
    entityName,
    'issues-observability-entity',
    RampIdType.EntityName
  );

  const value = envIsServerDeployment
    ? serverEnableCoreWeaveObservability()
    : userOrOrgFlag || entityFlag;

  useRecordFeatureFlag('issues_observability', value);
  return value;
};

export const useStatsigIssuesObservability = (orgId: string) => {
  const value = useOrgIdRampFlag(
    'coreweave_observability',
    RampIdType.OrgId,
    orgId
  );
  useRecordFeatureFlag('coreweave_observability', value);

  return value;
};

export const useStatsigGateRecoverDeletedRuns = () => {
  const value = useOrgIdRampFlag('recover-deleted-runs', RampIdType.OrgId);
  useRecordFeatureFlag('recover_deleted_runs', value);
  return value;
};

export const useRampFlagMediaPanelTracking = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'media-panel-tracking-entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'media-panel-tracking-user',
    RampIdType.UserName
  );
  const isOrgEnabled = useOrgRampFlag(
    'media-panel-tracking-org',
    RampIdType.OrgName
  );
  const isEnabled = isEntityEnabled || isUserEnabled || isOrgEnabled;
  useRecordFeatureFlag('media_panel_tracking', isEnabled);
  return isEnabled;
};

export const useRampFlagMediaPanelTrackingDebug = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'media_panel_tracking_debug_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'media_panel_tracking_debug_user',
    RampIdType.UserName
  );
  const isEnabled = isEntityEnabled || isUserEnabled;
  useRecordFeatureFlag('media_panel_tracking_debug', isEnabled);
  return isEnabled;
};

export const useRampFlagShowOrgActivityPage = () => {
  const isOrgEnabled = useOrgRampFlag(
    'show_org_activity_page',
    RampIdType.OrgId
  );
  const value = isOrgEnabled || false;
  useRecordFeatureFlag('show_org_activity_page', value);
  return value;
};

export const useStatsigGateCoreWeaveSaasByob = (
  organizationId?: string | null
) => {
  // Only used for Saas as setting up CoreWeave buckets in server is a different process
  const isOrgEnabled = useOrgIdRampFlag(
    'saas_coreweave_storage_byob',
    RampIdType.OrgId,
    organizationId
  );
  const value = envIsServerDeployment ? false : isOrgEnabled;
  useRecordFeatureFlag('saas_coreweave_storage_byob', value);
  return value;
};

export const useRampInferenceAsProduct = () => {
  const value = useOrgRampFlag('inference_as_product', RampIdType.OrgName);
  useRecordFeatureFlag('inference_as_product', value);
  return value;
};

export const useRampProPlanMonthlyOption = () => {
  const isUserEnabled = useUserRampFlag(
    'pro_plan_monthly_option',
    RampIdType.UserId
  );
  // never show for dedicated
  const value = envIsServerDeployment ? false : isUserEnabled;
  useRecordFeatureFlag('pro_plan_monthly_option', value);
  return value;
};

export const useRampFlagFullScreenLinePlotEditing = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'fullscreen_line_plot_editing_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserRampFlag(
    'fullscreen_line_plot_editing_user',
    RampIdType.UserName
  );
  const isDisabled = envIsIntegration || envIsServerDeployment;
  const entityValue = isDisabled ? false : isEntityEnabled;
  const userValue = isDisabled ? false : isUserEnabled;
  const isEnabled = entityValue || userValue;
  useRecordFeatureFlag('fullscreen_line_plot_editing', isEnabled);
  return isEnabled;
};

export const useRampFlagBRSDQWithoutRunInfo = (entityName: string) => {
  const {isEnabled: isUserEnabled, loading: isUserEnabledLoading} =
    useUserRampFlagWithLoading(
      'brsdq_without_run_info_user',
      RampIdType.UserId
    );
  const {data: isEntityEnabled, loading: isEntityEnabledLoading} =
    useEntityRampFlagWithLoading(
      entityName,
      'brsdq_without_run_info_entity',
      RampIdType.EntityId
    );
  const {data: isOrgEnabled, loading: isOrgEnabledLoading} =
    useOrgRampFlagWithLoading('brsdq_without_run_info_org', RampIdType.OrgId);

  const isLoading =
    isUserEnabledLoading || isEntityEnabledLoading || isOrgEnabledLoading;
  const definedFlags = [isUserEnabled, isEntityEnabled, isOrgEnabled].filter(
    flag => flag !== undefined
  );
  const isEnabled = isLoading ? undefined : definedFlags.some(flag => flag);

  useRecordFeatureFlag('brsdq_without_run_info', isEnabled);

  if (isLoading) {
    return {
      isEnabled: undefined,
      loading: true,
    };
  }

  return {
    isEnabled,
    loading: false,
  };
};

export const useStatsigGateSourceArtifactProtection = (
  organizationName: string
): {data: boolean; loading: boolean} => {
  const {data, loading} = useOrgNameRampFlag(
    organizationName,
    'source_artifact_protection',
    RampIdType.OrgName
  );
  useRecordFeatureFlag('source_artifact_protection', data);

  return {data, loading};
};

export const useStatsigGateAutomationHubFrontend = (
  organizationName: string
): {enabled: boolean; loading: boolean} => {
  const {data, loading} = useOrgNameRampFlag(
    organizationName,
    'automation_hub',
    RampIdType.OrgName
  );
  const enabled = (!loading && data) || false;
  useRecordFeatureFlag('automation_hub', enabled);

  return {
    enabled,
    loading,
  };
};

export const useStatsigGateAutomationExecutionHistory = (
  organizationId?: string | null
) => {
  const value = useOrgIdRampFlag(
    'automation_execution_history',
    RampIdType.OrgId,
    organizationId
  );
  useRecordFeatureFlag('automation_execution_history', value);
  return value;
};

export const useStatsigGateAutomationFree = (organizationId: string | null) => {
  /*
    Automations is now free for everyone
    Leaving in case we do want to rollback
  */
  return true;
};

export const useStatsigGateAutomationEntityScope = (
  organizationId?: string | null
) => {
  const value = useOrgIdRampFlag(
    'automation_entity_scope',
    RampIdType.OrgId,
    organizationId
  );
  useRecordFeatureFlag('automation_entity_scope', value);
  return value;
};

export const useIsAutomationAriaTriggerEnabled = (
  organizationId?: string | null
) => {
  const value = useOrgIdRampFlag(
    'automation_aria_trigger',
    RampIdType.OrgId,
    organizationId
  );
  useRecordFeatureFlag('automation_aria_trigger', value);
  return value;
};

export const useRampFlagWeaveDebouncedScoring = () => {
  const value = useOrgIdRampFlag('weave_debounced_scoring', RampIdType.OrgId);
  useRecordFeatureFlag('weave_debounced_scoring', value);
  return value;
};

export const useRampFlagWeaveRemoteScoring = (orgId?: string | null) => {
  const value = useOrgIdRampFlag(
    'weave_remote_scoring',
    RampIdType.OrgId,
    orgId ?? null
  );
  useRecordFeatureFlag('weave_remote_scoring', value);
  return value;
};

export const useStatsigGateBulkUserInvitations = () => {
  const value = useOrgIdRampFlag('bulk_org_invitations', RampIdType.OrgId);
  useRecordFeatureFlag('bulk_org_invitations', value);
  return value;
};

export const useStatsigGateWeaveTraceAnalysisFrontend = () => {
  const value = useOrgIdRampFlag(
    'weave_trace_analysis_frontend',
    RampIdType.OrgId
  );
  useRecordFeatureFlag('weave_trace_analysis_frontend', value);
  return value;
};

export const useStatsigGateHideWeaveMonitorsTab = () => {
  const value = useOrgIdRampFlag('hide_weave_monitors_tab', RampIdType.OrgId);
  useRecordFeatureFlag('hide_weave_monitors_tab', value);
  return value;
};

// Whether to show the workspace feedback button (opens the Chameleon feedback
// drawer). Off by default; enabled per-org in Statsig.
export const useStatsigGateWorkspaceFeedback = () => {
  const value = useOrgIdRampFlag('workspace_feedback_org', RampIdType.OrgId);
  useRecordFeatureFlag('workspace_feedback', value);
  return value;
};

export const useStatsigGateCWForgeUnification = () => {
  const value = useOrgIdRampFlag('cw_forge_unification', RampIdType.OrgId);
  useRecordFeatureFlag('cw_forge_unification', value);
  return value;
};

export const useRampFlagOrgAlertsTab = () => {
  const statsigOrgRampFlag = useOrgRampFlag('org_alerts_tab', RampIdType.OrgId);
  const value = envIsServerDeployment ? true : (statsigOrgRampFlag ?? false);
  useRecordFeatureFlag('org_alerts_tab', value);
  return value;
};

export const useStatsigGateSemanticLegendsBucketEditing = () => {
  const value = useOrgRampFlag(
    'semantic_legend_bucket_editing_org',
    RampIdType.OrgName
  );
  useRecordFeatureFlag('semantic_legend_bucket_editing', value);
  return value;
};

export const useStatsigGateScatterSemanticColors = () => {
  const value = useUserRampFlag(
    'scatter_plot_semantic_colors',
    RampIdType.UserId
  );
  const isEnabled = value ?? false;
  useRecordFeatureFlag('scatter_plot_semantic_colors', isEnabled);
  return isEnabled;
};

export const useStatsigGateScatterReferenceLines = () => {
  const value = useUserRampFlag(
    'scatter_plot_reference_lines_user',
    RampIdType.UserId
  );
  const isEnabled = value ?? false;
  useRecordFeatureFlag('scatter_plot_reference_lines', isEnabled);
  return isEnabled;
};

export const useStatsigGateScatterParetoFrontier = () => {
  const value = useUserRampFlag(
    'scatter_plot_pareto_frontier',
    RampIdType.UserId
  );
  const isEnabled = value ?? false;
  useRecordFeatureFlag('scatter_plot_pareto_frontier', isEnabled);
  return isEnabled;
};

export const useStatsigGateDisableHistoryKeysWithHashCache = () => {
  const value = useUserRampFlag(
    'disable_history_keys_with_hash_cache',
    RampIdType.UserId
  );
  const isEnabled = value ?? false;
  useRecordFeatureFlag('disable_history_keys_with_hash_cache', isEnabled);
  return isEnabled;
};

export const useStatsigGateCollectionOverviewPhase1WithLoading = (
  orgId: string
): {enabled: boolean; loading: boolean} => {
  const ret = useOrgRampFlagWithLoading(
    'collection_overview_phase_1',
    RampIdType.OrgId,
    orgId
  );
  const enabled = (!ret.loading && ret.data) || false;
  useRecordFeatureFlag('collection_overview_phase_1', enabled);

  return {
    enabled,
    loading: ret.loading,
  };
};

export const useStatsigGateCollectionOverviewPhase1 = (orgId: string) => {
  const {enabled} = useStatsigGateCollectionOverviewPhase1WithLoading(orgId);
  return enabled;
};

export const useStatsigGateLineageModernizationPhase2WithLoading = (
  orgId: string
): {enabled: boolean; loading: boolean} => {
  const ret = useOrgRampFlagWithLoading(
    'lineage_modernization_phase_2',
    RampIdType.OrgId,
    orgId
  );
  const enabled = (!ret.loading && ret.data) || false;
  useRecordFeatureFlag('lineage_modernization_phase_2', enabled);

  return {
    enabled,
    loading: ret.loading,
  };
};

export const useStatsigGateLineageModernizationPhase2 = (orgId: string) => {
  const {enabled} = useStatsigGateLineageModernizationPhase2WithLoading(orgId);
  return enabled;
};

export const useRampFlagRunLogsPagination = () => {
  const value = useUserRampFlag('run_logs_pagination', RampIdType.UserId);
  useRecordFeatureFlag('run_logs_pagination', value);
  return value;
};

export const useRampFlagVideoStreaming = (entityName: string) => {
  const isEntityEnabled = useEntityRampFlag(
    entityName,
    'video_streaming_entity',
    RampIdType.EntityId
  );
  useRecordFeatureFlag('video_streaming_entity', isEntityEnabled);
  return isEntityEnabled;
};

export const useRampFlagWorkspaceFilterVisibilityUI = () => {
  const value = useUserRampFlag(
    'workspace_filter_visibility_ui',
    RampIdType.UserId
  );
  const isEnabled = value ?? false;
  useRecordFeatureFlag('workspace_filter_visibility_ui', isEnabled);
  return isEnabled;
};

export const useRampFlagRunsTableResultsHiddenByFiltersBanner = () => {
  const value = useUserRampFlag(
    'runs_table_results_hidden_by_filters_banner',
    RampIdType.UserId
  );
  const isEnabled = value ?? false;
  useRecordFeatureFlag(
    'runs_table_results_hidden_by_filters_banner',
    isEnabled
  );
  return isEnabled;
};

export const useStatsigGateRegistryUIPhase3 = (orgId: string) => {
  const isOrgEnabled = useOrgIdRampFlag(
    'registry_ui_phase_3',
    RampIdType.OrgId,
    orgId
  );
  const value = isOrgEnabled || false;
  useRecordFeatureFlag('registry_ui_phase_3', value);
  return value;
};

export const useRampFlagEntityLessProfilePage = () => {
  const value = useUserRampFlag('entity_less_profile_page', RampIdType.UserId);
  const isEnabled = value ?? false;
  useRecordFeatureFlag('entity_less_profile_page', isEnabled);
  return isEnabled;
};

export const useRampFlagUPlotUser = () => {
  const value = useUserRampFlag('uplot_user', RampIdType.UserId);
  useRecordFeatureFlag('uplot_user', value);
  return value;
};

export const useRampFlagUnlimitedTrackedHoursForAll = () => {
  const value = useUserRampFlag(
    'unlimited_tracked_hours_for_all',
    RampIdType.UserId
  );
  useRecordFeatureFlag('unlimited_tracked_hours_for_all', value);
  return value;
};

export const useStatsigGateLinkWeaveToRegistry = () => {
  const value = useOrgRampFlag(
    'link_weave_prompts_to_registry',
    RampIdType.OrgId
  );
  useRecordFeatureFlag('link_weave_prompts_to_registry', value);
  return value;
};

export const useStatsigGateHideStopRunsUI = () => {
  const value = useUserRampFlag('hide_stop_run_ui', RampIdType.UserId);
  useRecordFeatureFlag('hide_stop_run_ui', value);
  return value;
};

export const useStatsigGateSmartFeatureSettings = (orgName: string) => {
  const {data} = useOrgNameRampFlag(
    orgName,
    'smart_feature_settings',
    RampIdType.OrgId
  );
  useRecordFeatureFlag('smart_feature_settings', data);
  return data;
};

/**
 * Hook for sparse panel overrides feature flag.
 * This flag controls whether the new sparse panel overrides system is used
 * (fractional indexing, override tracking, keeping isPanelsAuto=true).
 *
 * The hook also caches the result for synchronous access in Redux reducers.
 *
 * See also: useRampFlagSparseCompression for the compression/persistence flag.
 */
export const useRampFlagSparseOverrides = () => {
  const {
    isEnabled: isUserEnabled,
    loading,
    hasData,
  } = useUserRampFlagWithLoading(
    'sparse_panel_overrides_user',
    RampIdType.UserId
  );

  // Fail open: only an explicit false from a successful flags response
  // disables the feature. Loading, query failures, and a missing key all
  // resolve to true.
  const value = loading || !hasData ? true : (isUserEnabled ?? true);
  useRecordFeatureFlag('sparse_panel_overrides', value);

  // Cache the value for synchronous access in reducers
  setSparseOverridesEnabled(value);

  return value;
};

/**
 * Hook for sparse compression feature flag.
 * This flag controls whether auto-generated panels are stripped during
 * compression/save, reducing spec size.
 *
 * This is separate from useRampFlagSparseOverrides for phased rollout:
 * - Phase 1: Overrides ON, Compression OFF (safe rollback)
 * - Phase 2: Overrides ON, Compression ON (full sparse mode)
 *
 * The hook also caches the result for synchronous access in compression logic.
 */
export const useRampFlagSparseCompression = () => {
  const {
    isEnabled: isUserEnabled,
    loading,
    hasData,
  } = useUserRampFlagWithLoading('sparse_compression_user', RampIdType.UserId);

  // Fail open: only an explicit false from a successful flags response
  // disables the feature. Loading, query failures, and a missing key all
  // resolve to true.
  const value = loading || !hasData ? true : (isUserEnabled ?? true);
  useRecordFeatureFlag('sparse_compression', value);

  // Cache the value for synchronous access in compression logic
  setSparseCompressionEnabled(value);

  return value;
};

export const useStatsigGateWorkspaceAgent = () => {
  const value = useUserRampFlag('workspace_agent', RampIdType.UserId);
  useRecordFeatureFlag('workspace_agent', value);
  return value;
};

export const useStatsigGateARIAPersonalSecrets = (orgName: string) => {
  const {data} = useOrgNameRampFlag(
    orgName,
    'aria_personal_secrets',
    RampIdType.OrgId
  );
  useRecordFeatureFlag('aria_personal_secrets', data);
  return data;
};

export const useStatsigGateARIAProjectMemory = (orgName: string) => {
  const {data} = useOrgNameRampFlag(
    orgName,
    'aria_project_memory',
    RampIdType.OrgId
  );
  useRecordFeatureFlag('aria_project_memory', data);
  return data;
};

/**
 * Unified next-gen workspaces feature gate (truncated panel gen,
 * add panels drawer, panel metric pickers). True when
 * `ngw_truncated_panel_gen` is on, the current project's reads are served from
 * the RSA accelerator (`Project.runStoreAcceleratorReadEnabled`), and the
 * current project is listed as eligible in `ngw_truncated_panel_gen_limits`.
 */
export const useIsNgwEnabled = () => {
  const isEnabled = useUserRampFlag(
    'ngw_truncated_panel_gen',
    RampIdType.UserId
  );
  // NGW reads run history through the RSA accelerator, so it must only turn on
  // when reads for this project are actually served from the accelerator. The
  // backend resolves that (accelerator wired + backfill finished + read ramp on
  // for this user/project) and exposes it as one field, so we gate on it
  // directly instead of re-checking the ch_runs_read_from_accelerator ramp here.
  const {entityName, projectName} = useProjectParams();
  const isReadingFromAccelerator = useProjectRsaReadEnabled(
    entityName,
    projectName
  );
  const {isEligible} = useIsCurrentProjectNgwEligible();
  const isEnabledAndEligible =
    isEnabled === true && isReadingFromAccelerator && isEligible;
  setNgwEnabled(isEnabledAndEligible);
  useRecordFeatureFlag('ngw_enabled_and_eligible', isEnabledAndEligible);
  return isEnabledAndEligible;
};

/**
 * Whether the next-gen workspaces media picker is available to this user. The
 * picker is part of NGW, so it needs both the `ngw_media_picker` gate and
 * `useIsNgwEnabled` (NGW gate + RSA reads + project eligibility). Turning the
 * picker gate on for a user in a project that is not running NGW does nothing.
 */
export const useRampFlagNgwMediaPicker = () => {
  const isPickerEnabled = useUserRampFlag(
    'ngw_media_picker',
    RampIdType.UserId
  );
  const isNgwEnabled = useIsNgwEnabled();
  const value = isPickerEnabled === true && isNgwEnabled;
  useRecordFeatureFlag('ngw_media_picker', value);
  return value;
};

export const useStatsigGateCreateOrgScopedKeys = (orgId?: string | null) => {
  const value = useOrgIdRampFlag(
    'create-org-scoped-keys',
    RampIdType.OrgId,
    orgId ?? null
  );
  const isEnabled = value ?? false;
  useRecordFeatureFlag('create_org_scoped_keys', isEnabled);
  return isEnabled;
};

export const useRampFlagEnableWbAgentUsageCharts = (
  orgName: string
): {enabled: boolean; loading: boolean} => {
  const ret = useOrgNameRampFlag(
    orgName,
    'enable_wb_agent_usage_charts',
    RampIdType.OrgId
  );
  const enabled = (!ret.loading && ret.data) || false;
  useRecordFeatureFlag('enable_wb_agent_usage_charts', enabled);
  return {
    enabled,
    loading: ret.loading,
  };
};

export const useRampFlagMediaFilesQueryWithUrls = () => {
  const value = useUserRampFlag(
    'media_files_query_with_urls',
    RampIdType.UserId
  );
  useRecordFeatureFlag('media_files_query_with_urls', value);
  return value;
};

export const useRampFlagSandboxes = (orgId?: string | null) => {
  const value = useOrgIdRampFlag('sandboxes', RampIdType.OrgId, orgId ?? null);
  useRecordFeatureFlag('sandboxes', value);
  return value;
};

export const useStatsigGateReferenceBucketByob = (orgId?: string | null) => {
  const value = useOrgIdRampFlag(
    'reference_bucket_byob',
    RampIdType.OrgId,
    orgId ?? null
  );
  useRecordFeatureFlag('reference_bucket_byob', value);
  return value;
};
/**
 * Gates the Sandbox product row in the unified pay-as-you-go billing section.
 * Separate from `sandboxes` (which gates the sandbox product itself).
 */
export const useRampFlagSandboxPaygo = () => {
  const value = useUserRampFlag(
    'sandboxes_paygo_purchasing',
    RampIdType.UserId
  );
  useRecordFeatureFlag('sandboxes_paygo_purchasing', value);
  return value;
};

export const useRampFlagPaygoRevamp = () => {
  const value = useUserRampFlag('paygo_revamp', RampIdType.UserId);
  useRecordFeatureFlag('paygo_revamp', value);
  return value;
};

export const useRampFlagInferenceCachedUncachedCSV = () => {
  const value = useOrgIdRampFlag(
    'inference_csv_cached_uncached_columns',
    RampIdType.OrgId
  );
  useRecordFeatureFlag('inference_csv_cached_uncached_columns', value);
  return value;
};

export const useStatsigGateSlurmDaemon = (orgId?: string | null) => {
  const value = useOrgIdRampFlag(
    'wandb-slurm-daemon',
    RampIdType.OrgId,
    orgId ?? null
  );
  useRecordFeatureFlag('wandb_slurm_daemon', value);
  return value;
};

export const useRampFlagMarimoPanelsUser = () => {
  const value = useUserRampFlag('marimo_panels_user', RampIdType.UserId);
  useRecordFeatureFlag('marimo_panels_user', value);
  return value;
};

export const useRampFlagMarimoNotebookManagementWithLoading = (
  orgId?: string | null
) => {
  const {loading, data} = useOrgRampFlagWithLoading(
    'marimo_notebook_management',
    RampIdType.OrgId,
    orgId ?? null
  );
  useRecordFeatureFlag('marimo_notebook_management', !loading && data);
  return {isEnabled: data, loading};
};

export const useRampFlagMarimoNotebookManagement = (orgId?: string | null) => {
  const {isEnabled, loading} =
    useRampFlagMarimoNotebookManagementWithLoading(orgId);
  return loading ? undefined : isEnabled;
};

export const useRampFlagMarimoNotebookRuntime = () => {
  const isEnabled = useUserRampFlag(
    'marimo_notebook_runtime',
    RampIdType.UserId
  );
  const value = envIsDev || isEnabled;
  useRecordFeatureFlag('marimo_notebook_runtime', value);
  return value;
};

export const useRampFlagMarimoNotebooks = () => {
  const value = useUserRampFlag('marimo_notebooks', RampIdType.UserId);
  useRecordFeatureFlag('marimo_notebooks', value);
  return value;
};

export const useRampFlagTeamUsageChart = () => {
  const value = useUserRampFlag('team_usage_chart', RampIdType.UserId);
  useRecordFeatureFlag('team_usage_chart', value);
  return value;
};

export const useRampFlagRunSelectorPinOnHover = () => {
  const value = useUserRampFlag('run_selector_pin-on-hover', RampIdType.UserId);
  useRecordFeatureFlag('run_selector_pin_on_hover', value);
  return value;
};

export const useStatsigGateEvalTables = () => {
  const isEnabled = useOrgIdRampFlag('eval_tables', RampIdType.OrgId);
  const value = envIsServerDeployment ? true : isEnabled;
  useRecordFeatureFlag('eval_tables', value);
  return value;
};

export const useRampFlagShortcutDrawer = () => {
  const value = useUserRampFlag('shortcut_drawer', RampIdType.UserId);
  const isEnabled = value ?? false;
  useRecordFeatureFlag('shortcut_drawer', isEnabled);
  return isEnabled;
};

export const useStatsigGateEnableUserSecrets = (orgId?: string | null) => {
  // userOrgIdRampFlag will check the viewer's orgs for a matching orgID.
  // for single-tenant there may be no matching org in that list,
  // so orgID must be null to work as expected.
  const resolvedOrgId = envIsServerDeployment ? null : orgId;
  const userSecretsEnabled = useOrgIdRampFlag(
    'enable_user_secrets',
    RampIdType.OrgId,
    resolvedOrgId ?? null
  );

  // for a null orgID, useOrgIdRampFlag will check if any of the viewer's orgs has the flag enabled.
  // for multi-tenant, user secrets should not be enabled if no orgID is provided.
  const value =
    !envIsServerDeployment && (orgId == null || orgId.trim() === '')
      ? false
      : userSecretsEnabled;
  useRecordFeatureFlag('enable_user_secrets', value);
  return value;
};

export const useRampFlagMediaCompareModeDiscovery = () => {
  const value = useUserRampFlag(
    'media_compare_mode_discovery',
    RampIdType.UserId
  );
  useRecordFeatureFlag('media_compare_mode_discovery', value);
  return value;
};

export const useRampFlagSelfServiceCancellation = () => {
  const value = useUserRampFlag('self_service_cancellation', RampIdType.UserId);
  useRecordFeatureFlag('self_service_cancellation', value);
  return value;
};

export const useStatsigGateMobileApiKeyLogin = () => {
  const value = useOrgIdRampFlag(
    'mobile_api_key_login_deployment',
    RampIdType.OrgId
  );
  useRecordFeatureFlag('mobile_api_key_login_deployment', value);
  return value;
};
